import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import dayjs from "dayjs";
import useApiStable from "../../utils/useApiStable";
import "../../styles/BookingsOverview.css";
import {useAuth} from "../../context/AuthContext";
import HeatingTimeline from "./HeatingTimeline";
import {canManageHeating} from "../../utils/userUtils";

const SLOT_MINUTES = 30;
const SLOT_PX = 24;
const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const normalizeDateTime = (value) => {
    if (value == null) return null;
    const trimmed = `${value}`.trim();
    const direct = dayjs(trimmed);
    if (direct.isValid()) {
        return direct.format(DATE_TIME_FORMAT);
    }
    const match = trimmed.match(/^([+-]?)(\d+)-(.*)$/);
    if (match) {
        const digits = match[2];
        const tail = match[3];
        const year = digits.slice(-4);
        const candidate = `${year}-${tail}`;
        const parsed = dayjs(candidate);
        if (parsed.isValid()) {
            return parsed.format(DATE_TIME_FORMAT);
        }
    }
    return null;
};

const toSortableTimestamp = (value) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY;
};

// Neue Hilfsfunktion: Court-Label auf eine sinnvolle Sortiernummer (z.B. "Platz 4" -> 4) mappen
const extractCourtSortNumber = (label) => {
    if (!label) return null;
    const s = String(label);
    const m = s.match(/(\d+)/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
};

const defaultForm = (courtName = "") => ({
    id: null,
    title: "",
    court: courtName,
    start: dayjs().minute(0).second(0).add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    end: dayjs().minute(0).second(0).add(2, "hour").format("YYYY-MM-DDTHH:mm"),
    men: false,
    women: false,
    kneipe: false,
});

const BookingsOverview = ({
    isMatchBookingMode = false,
    onMatchSlotConfirm,
    onMatchSlotCancel,
    tournamentCourtGroupId = null,
}) => {
    const api = useApiStable();
    const {user} = useAuth();
    const [date, setDate] = useState(dayjs());
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [courtGroups, setCourtGroups] = useState([]);
    const [selectedCourtGroupId, setSelectedCourtGroupId] = useState("");
    // Hinweis wenn Turnier-CourtGroup am Datum nicht aktiv ist
    const [tournamentCourtGroupUnavailable, setTournamentCourtGroupUnavailable] = useState(false);

    const [courtOptions, setCourtOptions] = useState([]);
    const [form, setForm] = useState(() => defaultForm());
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [duration, setDuration] = useState(60); // Dauer in Minuten für Match-Buchung
    const [matchSelection, setMatchSelection] = useState(null);
    const canAccessHeating = useMemo(() => canManageHeating(user), [user]);

    const fetchRaw = useMemo(() => api.fetchRaw, [api.baseURL]);

    const bookingsAbortRef = useRef(null);
    const bookingsInFlightRef = useRef(false);
    const courtGroupsAbortRef = useRef(null);
    const courtGroupsInFlightRef = useRef(false);

    // Neue Memo-Variable: gefilterte Court-Gruppen nach activeTimes und aktuellem Datum
    const activeCourtGroups = useMemo(() => {
        if (!courtGroups?.length) return [];
        const target = date.startOf("day");
        return courtGroups.filter((group) => {
            const windows = group?.activeTimes;
            if (!windows || !Array.isArray(windows) || windows.length === 0) {
                // Ohne Konfiguration bleibt die Gruppe sichtbar
                return false;
            }
            return windows.some((w) => {
                if (!w) return false;
                const {startDate, endDate} = w;
                if (!startDate) return true;
                const start = startDate ? dayjs(startDate).startOf("day") : null;
                const end = endDate ? dayjs(endDate).endOf("day") : null;
                if (start && target.isBefore(start)) return false;
                if (end && target.isAfter(end)) return false;
                return true;
            });
        });
    }, [courtGroups, date]);

    const requiredTournamentCourtGroupId = useMemo(() => {
        const normalized = tournamentCourtGroupId != null ? String(tournamentCourtGroupId).trim() : "";
        return normalized || null;
    }, [tournamentCourtGroupId]);

    const getNextActiveDateForCourtGroup = useCallback((courtGroupId, fromDate) => {
        if (!courtGroupId) return null;
        const group = courtGroups.find((g) => String(g.id) === String(courtGroupId));
        const windows = group?.activeTimes;
        if (!windows || !Array.isArray(windows) || windows.length === 0) {
            // keine Konfiguration -> keine Einschränkung
            return fromDate;
        }

        const startOfFrom = dayjs(fromDate).startOf("day");
        // Suche das früheste StartDate, das nach dem aktuellen Tag liegt.
        let best = null;
        windows.forEach((w) => {
            if (!w) return;
            const start = w.startDate ? dayjs(w.startDate).startOf("day") : null;
            const end = w.endDate ? dayjs(w.endDate).endOf("day") : null;

            // Wenn wir schon in einem Fenster sind, dann ist "fromDate" ok.
            if (start && startOfFrom.isBefore(start)) {
                if (!best || start.isBefore(best)) best = start;
                return;
            }
            if (!start && end && startOfFrom.isAfter(end)) {
                return;
            }
            // start fehlt => immer ab "-infty" aktiv, also fromDate ok
            if (!start) {
                if (!end || !startOfFrom.isAfter(end)) {
                    best = startOfFrom;
                }
            }
        });
        return best;
    }, [courtGroups]);

    // Ausgewählte Court-Gruppe immer auf eine aktive Gruppe begrenzen
    useEffect(() => {
        // Turniermodus: CourtGroup fest an Turnier binden
        if (requiredTournamentCourtGroupId) {
            if (selectedCourtGroupId !== requiredTournamentCourtGroupId) {
                setSelectedCourtGroupId(requiredTournamentCourtGroupId);
            }

            // Prüfe, ob die Turnier-CourtGroup für dieses Datum aktiv ist
            const active = activeCourtGroups.some((g) => String(g.id) === String(requiredTournamentCourtGroupId));
            setTournamentCourtGroupUnavailable(!active);
            return;
        }

        setTournamentCourtGroupUnavailable(false);

        if (!activeCourtGroups.length) {
            if (selectedCourtGroupId) {
                setSelectedCourtGroupId("");
            }
            return;
        }
        const stillValid = selectedCourtGroupId && activeCourtGroups.some((g) => g.id === selectedCourtGroupId);
        if (!stillValid) {
            setSelectedCourtGroupId(activeCourtGroups[0].id || "");
        }
    }, [activeCourtGroups, requiredTournamentCourtGroupId, selectedCourtGroupId]);

    // Wenn Turnier-CourtGroup aktuell nicht aktiv ist: auf Button-Klick zum nächsten aktiven Datum springen
    const jumpToNextTournamentBookableTime = useCallback(() => {
        if (!requiredTournamentCourtGroupId) return;
        const next = getNextActiveDateForCourtGroup(requiredTournamentCourtGroupId, date);
        if (next && dayjs(next).isValid()) {
            setDate(dayjs(next));
        }
    }, [date, getNextActiveDateForCourtGroup, requiredTournamentCourtGroupId]);

    const courtLabel = useCallback((court) => {
        if (!court) return "";
        return court.name || court.title || court.id || "";
    }, []);

    const courtNames = useMemo(() => courtOptions.map((court) => courtLabel(court)).filter(Boolean), [courtOptions, courtLabel]);

    const courtNameById = useMemo(() => {
        const map = {};
        courtOptions.forEach((court) => {
            if (court?.id == null) return;
            map[String(court.id)] = courtLabel(court) || String(court.id);
        });
        return map;
    }, [courtOptions, courtLabel]);

    const courtIdByName = useMemo(() => {
        const map = {};
        courtOptions.forEach((court) => {
            const label = courtLabel(court);
            if (label) {
                map[label] = String(court.id);
            }
        });
        return map;
    }, [courtOptions, courtLabel]);

    const getDefaultForm = useCallback(() => defaultForm(courtNames[0] || ""), [courtNames]);

    const deriveBookingCourtId = useCallback((booking) => {
        if (!booking) return null;
        const candidate = booking.resourceId || booking.resource || booking.courtId || booking.locationId;
        if (candidate != null && `${candidate}`.trim() !== "") {
            return String(candidate);
        }
        if (booking.court && courtIdByName[booking.court]) {
            return courtIdByName[booking.court];
        }
        return null;
    }, [courtIdByName]);

    const isSameDay = useCallback((booking, target) => {
        const raw = booking.start || booking.startTime;
        const parsed = dayjs(raw);
        if (!parsed.isValid()) {
            return false;
        }
        return parsed.isSame(target, "day");
    }, []);

    const fetchBookings = useCallback(async () => {
        if (!selectedCourtGroupId) {
            setBookings([]);
            return;
        }
        if (bookingsInFlightRef.current) return;
        bookingsInFlightRef.current = true;

        if (bookingsAbortRef.current) {
            try {
                bookingsAbortRef.current.abort();
            } catch (_) {
            }
        }
        bookingsAbortRef.current = new AbortController();

        setLoading(true);
        setError("");
        try {
            const qs04 = `?date=${date.format("DD.MM.YYYY")}&courtGroup=${encodeURIComponent(selectedCourtGroupId)}`;
            const start = dayjs(date).startOf("day").toISOString();
            const end = dayjs(date).endOf("day").toISOString();
            const qsDb = `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&courtGroup=${encodeURIComponent(selectedCourtGroupId)}`;

            const [tennis04Resp, dbResp] = await Promise.all([
                fetchRaw(`/bookings/tennis04${qs04}`, {method: "GET", signal: bookingsAbortRef.current.signal}),
                fetchRaw(`/bookings/db${qsDb}`, {method: "GET", signal: bookingsAbortRef.current.signal}),
            ]);

            const [tennis04, local] = await Promise.all([tennis04Resp.json(), dbResp.json()]);
            const tennis04Arr = Array.isArray(tennis04) ? tennis04 : (tennis04 && typeof tennis04 === "object" ? Object.values(tennis04) : []);
            const localArr = Array.isArray(local) ? local : (local && typeof local === "object" ? Object.values(local) : []);

            const merged = [...tennis04Arr, ...localArr]
                .map((b) => {
                    const rawStart = b.startTime || b.start;
                    const rawEnd = b.endTime || b.end;
                    const normalizedStart = normalizeDateTime(rawStart);
                    const normalizedEnd = normalizeDateTime(rawEnd);
                    const startTime = normalizedStart || rawStart;
                    const endTime = normalizedEnd || rawEnd;

                    const inferredCourt = b.court || b.resource || b.location || "";
                    const rawResourceId = b.resourceId || b.resource || b.courtId || b.locationId;
                    let resourceId = rawResourceId ? String(rawResourceId) : null;
                    if (!resourceId && inferredCourt && courtIdByName[inferredCourt]) {
                        resourceId = courtIdByName[inferredCourt];
                    }
                    const resolvedCourtName = resourceId
                        ? (courtNameById[resourceId] || inferredCourt || resourceId)
                        : (inferredCourt || "Unbekannt");

                    const tennis04Id = user?.tennis04Id;
                    const username = (user?.username || "").toLowerCase();
                    const name = (user?.name || "").toLowerCase();
                    const players = Array.isArray(b.players) ? b.players : (b.players && typeof b.players === "object" ? Object.values(b.players) : []);

                    const isMine = !!(
                        tennis04Id && players?.some((p) => Number(p.idWithinOrganisation) === Number(tennis04Id))
                    ) || !!(
                        username && players?.some((p) => (p.email || "").toLowerCase() === username)
                    ) || !!(
                        name && players?.some((p) => (`${p.firstName || ""} ${p.lastName || ""}`.trim().toLowerCase() === name))
                    );

                    return {
                        ...b,
                        start: startTime,
                        end: endTime,
                        origin: b.origin || (b.id ? "LOCAL" : "REMOTE"),
                        court: resolvedCourtName,
                        resourceId,
                        isMine,
                    };
                })
                .sort((a, b) => toSortableTimestamp(a.start) - toSortableTimestamp(b.start));

            setBookings(merged);
        } catch (err) {
            if (err?.name !== "AbortError") {
                setError(err.message || "Fehler beim Laden der Buchungen");
            }
        } finally {
            setLoading(false);
            bookingsInFlightRef.current = false;
        }
    }, [courtIdByName, courtNameById, date, fetchRaw, selectedCourtGroupId, user]);

    const fetchCourtGroups = useCallback(async () => {
        if (courtGroupsInFlightRef.current) return;
        courtGroupsInFlightRef.current = true;

        if (courtGroupsAbortRef.current) {
            try {
                courtGroupsAbortRef.current.abort();
            } catch (_) {
            }
        }
        courtGroupsAbortRef.current = new AbortController();

        try {
            const resp = await fetchRaw("/courts/courtGroups", {
                method: "GET",
                signal: courtGroupsAbortRef.current.signal,
            });
            const payload = await resp.json();
            const groups = Array.isArray(payload) ? payload : (payload && typeof payload === "object" ? Object.values(payload) : []);
            setCourtGroups(groups);
            // Initiale Vorauswahl nur über activeCourtGroups im Effekt oben
        } catch (err) {
            if (err?.name !== "AbortError") {
                setError((prev) => prev || err.message || "Fehler beim Laden der Court-Gruppen");
            }
        } finally {
            courtGroupsInFlightRef.current = false;
        }
    }, [fetchRaw]);

    useEffect(() => {
        fetchBookings();
        return () => {
            if (bookingsAbortRef.current) {
                try {
                    bookingsAbortRef.current.abort();
                } catch (_) {
                }
            }
            bookingsInFlightRef.current = false;
        };
    }, [fetchBookings]);

    useEffect(() => {
        fetchCourtGroups();
        return () => {
            if (courtGroupsAbortRef.current) {
                try {
                    courtGroupsAbortRef.current.abort();
                } catch (_) {
                }
            }
            courtGroupsInFlightRef.current = false;
        };
    }, [fetchCourtGroups]);

    useEffect(() => {
        if (!selectedCourtGroupId) {
            setCourtOptions([]);
            return;
        }
        const selectedGroup = courtGroups.find((group) => group.id === selectedCourtGroupId);
        if (selectedGroup?.courts?.length) {
            const normalized = [...selectedGroup.courts]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((court) => {
                    const id = court.id != null ? String(court.id) : (court.name || court.title || "").trim();
                    if (!id) return null;
                    return {
                        id,
                        name: court.name || court.title || id,
                        title: court.title,
                        order: court.order ?? 0,
                    };
                })
                .filter(Boolean);
            setCourtOptions(normalized);
        } else {
            setCourtOptions([]);
        }
    }, [courtGroups, selectedCourtGroupId]);

    useEffect(() => {
        setForm((prev) => {
            if (!courtNames.length) {
                return prev.court ? {...prev, court: ""} : prev;
            }
            if (prev.court && courtNames.includes(prev.court)) {
                return prev;
            }
            return {...prev, court: courtNames[0]};
        });
    }, [courtNames]);

    const slots = useMemo(() => {
        const start = dayjs(date).startOf("day").hour(6);
        const end = dayjs(date).startOf("day").hour(24);
        const list = [];
        let cursor = start;
        while (cursor.isBefore(end) || cursor.isSame(end)) {
            list.push(cursor);
            cursor = cursor.add(SLOT_MINUTES, "minute");
        }
        return list;
    }, [date]);

    const timelineCourts = useMemo(() => {
        // Baseline aus CourtGroup-Konfiguration (inkl. order)
        const base = courtOptions
            .map((court) => {
                const id = String(court.id);
                const label = courtLabel(court) || id;
                return {
                    id,
                    label,
                    order: court.order ?? null,
                    sortNumber: extractCourtSortNumber(label),
                };
            });

        const seen = new Set(base.map((court) => court.id));
        let needsFallback = false;
        const enriched = [...base];

        // Ergänze Courts, die nur über Buchungen bekannt sind
        bookings.forEach((booking) => {
            const resolvedId = deriveBookingCourtId(booking);
            if (resolvedId) {
                if (!seen.has(resolvedId)) {
                    seen.add(resolvedId);
                    const label = courtNameById[resolvedId] || booking.court || resolvedId;
                    enriched.push({
                        id: resolvedId,
                        label,
                        order: null,
                        sortNumber: extractCourtSortNumber(label),
                    });
                }
            } else if (booking.court) {
                needsFallback = true;
            }
        });

        if (needsFallback && !seen.has("__unassigned")) {
            enriched.push({id: "__unassigned", label: "Sonstige", order: Number.POSITIVE_INFINITY, sortNumber: null});
        }
        if (!enriched.length) {
            enriched.push({id: "__placeholder", label: "Platz", order: 0, sortNumber: null});
        }

        // WICHTIG: stabile Sortierung, damit Außenplätze 1-4 immer nebeneinander bleiben
        enriched.sort((a, b) => {
            const aOrder = a.order ?? Number.POSITIVE_INFINITY;
            const bOrder = b.order ?? Number.POSITIVE_INFINITY;
            if (aOrder !== bOrder) return aOrder - bOrder;

            const aNum = a.sortNumber ?? Number.POSITIVE_INFINITY;
            const bNum = b.sortNumber ?? Number.POSITIVE_INFINITY;
            if (aNum !== bNum) return aNum - bNum;

            return String(a.label).localeCompare(String(b.label), undefined, {numeric: true, sensitivity: "base"});
        });

        // Rückgabe-Shape beibehalten
        return enriched.map(({id, label}) => ({id, label}));
    }, [bookings, courtLabel, courtNameById, courtOptions, deriveBookingCourtId]);

    const eventsByCourt = useMemo(() => {
        const map = timelineCourts.reduce((acc, court) => {
            acc[court.id] = [];
            return acc;
        }, {});
        const fallbackId = timelineCourts[0]?.id;
        bookings.forEach((booking) => {
            let targetId = deriveBookingCourtId(booking);
            if (!targetId || !map[targetId]) {
                targetId = fallbackId;
            }
            if (!targetId) return;
            map[targetId].push(booking);
        });
        return map;
    }, [bookings, timelineCourts, deriveBookingCourtId]);

    const openForm = useCallback((booking = null, slotDay = date, slotTime = null, court = courtNames[0] || "") => {
        if (isMatchBookingMode) return; // Im Match-Modus kein Formular öffnen
        if (booking) {
            setForm({
                id: booking.id || null,
                title: booking.title || "",
                court: booking.court || court,
                start: dayjs(booking.start).format("YYYY-MM-DDTHH:mm"),
                end: dayjs(booking.end).format("YYYY-MM-DDTHH:mm"),
                men: !!booking.men,
                women: !!booking.women,
                kneipe: !!booking.kneipe,
            });
        } else {
            const start = slotTime || dayjs(slotDay).hour(dayjs().hour()).minute(0).second(0);
            const fallbackCourt = court && courtNames.includes(court) ? court : (courtNames[0] || "");
            setForm({
                ...getDefaultForm(),
                court: fallbackCourt,
                start: dayjs(slotDay).hour(start.hour()).minute(start.minute()).format("YYYY-MM-DDTHH:mm"),
                end: dayjs(slotDay).hour(start.hour()).minute(start.minute()).add(1, "hour").format("YYYY-MM-DDTHH:mm"),
            });
        }
        setShowForm(true);
    }, [courtNames, date, getDefaultForm]);

    const closeForm = useCallback(() => {
        setShowForm(false);
        setForm(getDefaultForm());
    }, [getDefaultForm]);

    const handleSave = useCallback(async (evt) => {
        evt.preventDefault();
        setSaving(true);
        setError("");
        try {
            const courtOption = courtOptions.find((c) => String(courtLabel(c)) === String(form.court));
            const payload = {
                title: form.title,
                courtId: courtOption ? courtOption.id : null,
                startTime: dayjs(form.start).toISOString(),
                endTime: dayjs(form.end).toISOString(),
                men: form.men,
                women: form.women,
                kneipe: form.kneipe,
            };

            if (form.id) {
                await fetchRaw(`/bookings/${form.id}`, {
                    method: "PUT",
                    body: JSON.stringify(payload),
                });
            } else {
                await fetchRaw("/bookings/createBooking", {
                    method: "POST",
                    body: JSON.stringify(payload),
                });
            }

            closeForm();
            fetchBookings();
        } catch (err) {
            setError(err.message || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    }, [closeForm, fetchBookings, fetchRaw, form, courtOptions, courtLabel]);

    const handleDelete = useCallback(async (bookingId) => {
        if (!bookingId) return;
        if (!window.confirm("Buchung wirklich löschen?")) return;
        try {
            await fetchRaw(`/bookings/${bookingId}`, {method: "DELETE"});
            fetchBookings();
        } catch (err) {
            setError(err.message || "Löschen fehlgeschlagen");
        }
    }, [fetchBookings, fetchRaw]);

    const dayStartForGrid = useMemo(() => dayjs(date).startOf("day").hour(6).minute(0).second(0), [date]);

    const renderEvent = useCallback((booking) => {
        const durationMin = Math.max(0, dayjs(booking.end).diff(dayjs(booking.start), "minute"));
        const slotHeight = (durationMin / SLOT_MINUTES) * SLOT_PX;
        const offsetMin = Math.max(0, dayjs(booking.start).diff(dayStartForGrid, "minute"));
        const topPx = (offsetMin / SLOT_MINUTES) * SLOT_PX;

        const isSelection = booking.isMatchSelection;
        const eventKey = isSelection ? `selection-${booking.start}` : (booking.identifier || booking.id || `${booking.resourceId || booking.court}-${booking.start}`);

        const playersArray = Array.isArray(booking.players)
            ? booking.players
            : (booking.players && typeof booking.players === "object" ? Object.values(booking.players) : []);
        const firstPlayer = playersArray?.[0];
        const displayTitle = isSelection
            ? "Gewählter Slot"
            : (firstPlayer
                ? `${firstPlayer.firstName || ""} ${firstPlayer.lastName || ""}`.trim() || (booking.title || "(ohne Titel)")
                : (booking.title || "(ohne Titel)"));
        const playerNames = playersArray
            .map((p) => `${p.firstName || ""} ${p.lastName || ""}`.trim())
            .filter(Boolean)
            .join(", ");
        const tooltip = [
            booking.court || "",
            `${dayjs(booking.start).format("HH:mm")} - ${dayjs(booking.end).format("HH:mm")}`,
            playerNames,
        ].filter(Boolean).join("\n");

        return (
            <div
                key={eventKey}
                className={`booking-event ${isSelection ? 'match-selection-event' : ''} ${booking.origin === "REMOTE" ? "booking-remote" : "booking-local"} ${booking.isMine ? "booking-mine" : ""}`}
                style={{height: slotHeight, top: topPx}}
                title={tooltip}
                onClick={() => !isSelection && openForm(booking)}
            >
                <strong>{displayTitle}</strong>
                <div className="booking-event-time">
                    {dayjs(booking.start).format("HH:mm")} - {dayjs(booking.end).format("HH:mm")}
                </div>
                {playersArray?.length > 0 && (
                    <div className="booking-event-meta">
                        {playersArray.map((p, idx) => (
                            <div key={p.playerId || p.id || idx} className="booking-event-player">
                                {`${p.firstName || ""} ${p.lastName || ""}`.trim()}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }, [dayStartForGrid, openForm]);

    const renderDayColumn = useCallback((day, heatingRooms, heatingByRoom, renderHeatingEvent) => {
        const dayEventsByCourt = Object.entries(eventsByCourt).reduce((acc, [courtId, events]) => {
            acc[courtId] = events.filter(event => isSameDay(event, day));
            return acc;
        }, {});

        // Füge die temporäre Auswahl hinzu, wenn sie für diesen Tag und Platz ist
        if (matchSelection && day.isSame(dayjs(matchSelection.start), 'day')) {
            if (!dayEventsByCourt[matchSelection.courtId]) {
                dayEventsByCourt[matchSelection.courtId] = [];
            }
            dayEventsByCourt[matchSelection.courtId].push({
                ...matchSelection,
                isMatchSelection: true,
            });
        }

        return (
            <div
                key={day.format("YYYY-MM-DD")}
                className="booking-day-column"
            >
                <div className="booking-day-header">{day.format("ddd, DD.MM.")}</div>
                <div className="booking-grid">
                    <div className="booking-time-col">
                        {slots.map((slot) => (
                            <div key={`${day.format("YYYY-MM-DD")}-${slot.format("HH:mm")}`} className="booking-slot-time">
                                {slot.minute() === 0 ? slot.format("HH:mm") : ""}
                            </div>
                        ))}
                    </div>

                    <div className="booking-resources">
                        <div className="booking-courts-col">
                            {timelineCourts.map((court) => (
                                <div key={`${day.format("YYYY-MM-DD")}-${court.id}`} className="booking-court-col">
                                    <div className="booking-court-title">{court.label}</div>
                                    <div
                                        className="booking-court-body"
                                        onClick={(e) => {
                                            if (!isMatchBookingMode) return;
                                            setError(""); // Reset error on new click
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const y = e.clientY - rect.top;
                                            const index = Math.floor(y / SLOT_PX);
                                            if (index < 0 || index >= slots.length) return;

                                            const start = slots[index];
                                            const end = start.add(duration, 'minute');

                                            // Collision detection
                                            const existingBookingsOnCourt = eventsByCourt[court.id] || [];
                                            const isOverlapping = existingBookingsOnCourt.some(booking => {
                                                const bookingStart = dayjs(booking.start);
                                                const bookingEnd = dayjs(booking.end);
                                                return start.isBefore(bookingEnd) && end.isAfter(bookingStart);
                                            });

                                            if (isOverlapping) {
                                                setError("Der ausgewählte Zeitraum ist bereits belegt oder überschneidet sich mit einer anderen Buchung.");
                                                setMatchSelection(null);
                                                return;
                                            }

                                            setMatchSelection({
                                                start: start.toISOString(),
                                                end: end.toISOString(),
                                                courtId: court.id,
                                                courtLabel: court.label,
                                            });
                                        }}
                                        onDoubleClick={(e) => {
                                            if (isMatchBookingMode) return;
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const y = e.clientY - rect.top;
                                            const index = Math.floor(y / SLOT_PX);
                                            const slotTime = slots[index] || day;
                                            openForm(null, day, slotTime, court.label);
                                        }}
                                    >
                                        {slots.map((slot) => (
                                            <div key={`${court.id}-${slot.format("HH:mm")}`} className="booking-slot"/>
                                        ))}
                                        {(dayEventsByCourt[court.id] || [])
                                            .map((booking) => renderEvent(booking))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        { !isMatchBookingMode && (
                            <div className="booking-heating-col">
                                {heatingRooms.map((room) => (
                                    <div key={`${day.format("YYYY-MM-DD")}-${room}`} className="booking-heating-room">
                                        <div className="booking-heating-title">{room}</div>
                                        <div className="booking-heating-body">
                                            {slots.map((slot) => (
                                                <div key={`${room}-${slot.format("HH:mm")}`} className="booking-heating-slot"/>
                                            ))}
                                            {(heatingByRoom[room] || [])
                                                .filter((slot) => isSameDay(slot, day))
                                                .map((slot) => renderHeatingEvent(slot))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }, [eventsByCourt, isSameDay, openForm, renderEvent, slots, timelineCourts, isMatchBookingMode, duration, matchSelection]);

    const handleHeatingError = useCallback((message) => {
        setError((prev) => prev || message);
    }, []);

    const renderView = useCallback((heatingRooms, heatingByRoom, renderHeatingEvent) => (
        <div className="bookings-view day">
            {renderDayColumn(date, heatingRooms, heatingByRoom, renderHeatingEvent)}
        </div>
    ), [date, renderDayColumn]);

    const renderToolbar = () => (
        <div className="bookings-toolbar">
            <div className="toolbar-left">
                <button className="primary" onClick={() => setDate(dayjs())}>Heute</button>
                <button onClick={() => setDate((d) => d.add(-1, "day"))}>&lt;</button>
                <button onClick={() => setDate((d) => d.add(1, "day"))}>&gt;</button>
                <input type="date" value={date.format("YYYY-MM-DD")} onChange={(e) => setDate(dayjs(e.target.value))}/>
                <label className="toolbar-court-group">
                    Court-Gruppe
                    <select
                        value={selectedCourtGroupId}
                        onChange={(e) => setSelectedCourtGroupId(e.target.value)}
                        disabled={!!requiredTournamentCourtGroupId || !activeCourtGroups.length}
                        title={requiredTournamentCourtGroupId ? "Für Turniere ist die Court-Gruppe fest vorgegeben." : undefined}
                    >
                        {!activeCourtGroups.length && <option value="">Keine Gruppen</option>}
                        {activeCourtGroups.map((group) => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                    </select>
                </label>
                {requiredTournamentCourtGroupId && tournamentCourtGroupUnavailable && (
                    <div style={{marginLeft: 12, display: "flex", gap: 8, alignItems: "center"}}>
                        <span style={{color: "#b26a00", fontSize: 13}}>
                            Die Court-Gruppe des Turniers ist am gewählten Datum nicht aktiv.
                        </span>
                        <button type="button" className="primary" onClick={jumpToNextTournamentBookableTime}>
                            Zur nächsten buchbaren Zeit
                        </button>
                    </div>
                )}
                {isMatchBookingMode && (
                    <label className="toolbar-duration">
                        Dauer
                        <select value={duration} onChange={e => setDuration(Number(e.target.value))}>
                            <option value={30}>30 min</option>
                            <option value={60}>60 min</option>
                            <option value={90}>90 min</option>
                            <option value={120}>120 min</option>
                        </select>
                    </label>
                )}
            </div>
            <div className="toolbar-center" />
            <div className="toolbar-right">
                {isMatchBookingMode && matchSelection ? (
                    <>
                        <button onClick={() => {
                            setMatchSelection(null);
                            if (onMatchSlotCancel) onMatchSlotCancel();
                        }}>Abbrechen</button>
                        <button className="primary" onClick={() => {
                            if (onMatchSlotConfirm) onMatchSlotConfirm(matchSelection);
                        }}>Bestätigen</button>
                    </>
                ) : (
                    <>
                        {!isMatchBookingMode && <button className="primary" onClick={() => openForm()}>Neue Buchung</button>}
                        <button onClick={fetchBookings} disabled={loading}>{loading ? "Lädt..." : "Aktualisieren"}</button>
                    </>
                )}
            </div>
        </div>
    );

    const mainContent = (heatingProps) => (
        <div className="bookings-page" {...(isMatchBookingMode ? {"data-no-pull-refresh": ""} : {})}>
            {renderToolbar()}
            {error && <div className="bookings-error">{error}</div>}
            {renderView(heatingProps?.heatingRooms || [], heatingProps?.heatingByRoom || {}, heatingProps?.renderHeatingEvent || (() => null))}
            {!isMatchBookingMode && <div className="booking-hint">Doppelklick in einen freien Slot erstellt eine Buchung für diesen Zeitpunkt.</div>}
            {showForm && (
                <div className="booking-modal">
                    <div className="booking-modal-content">
                        <h3>{form.id ? "Buchung bearbeiten" : "Neue Buchung"}</h3>
                        <form onSubmit={handleSave} className="booking-form">
                            <label>
                                Titel
                                <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required/>
                            </label>
                            <label>
                                Platz
                                <select value={form.court} onChange={(e) => setForm({...form, court: e.target.value})} disabled={!courtNames.length}>
                                    {!courtNames.length && <option value="">Keine Courts verfügbar</option>}
                                    {courtNames.map((name) => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Start
                                <input type="datetime-local" value={form.start} onChange={(e) => setForm({...form, start: e.target.value})} required/>
                            </label>
                            <label>
                                Ende
                                <input type="datetime-local" value={form.end} onChange={(e) => setForm({...form, end: e.target.value})} required/>
                            </label>
                            <div className="booking-form-flags">
                                <label><input type="checkbox" checked={form.men} onChange={(e) => setForm({...form, men: e.target.checked})}/> Herren</label>
                                <label><input type="checkbox" checked={form.women} onChange={(e) => setForm({...form, women: e.target.checked})}/> Damen</label>
                                <label><input type="checkbox" checked={form.kneipe} onChange={(e) => setForm({...form, kneipe: e.target.checked})}/> Kneipe</label>
                            </div>
                            <div className="booking-form-actions">
                                {form.id && (
                                    <button type="button" className="danger" onClick={() => handleDelete(form.id)}>Löschen</button>
                                )}
                                <button type="button" onClick={closeForm}>Abbrechen</button>
                                <button type="submit" className="primary" disabled={saving}>{saving ? "Speichern..." : "Speichern"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    if (isMatchBookingMode || !canAccessHeating) {
        return mainContent({});
    }

    return (
        <HeatingTimeline
            date={date}
            fetchRaw={fetchRaw}
            slotMinutes={SLOT_MINUTES}
            slotHeight={SLOT_PX}
            dayStartForGrid={dayStartForGrid}
            onError={handleHeatingError}
            courtGroupName={selectedCourtGroupId}
        >
            {(heatingProps) => mainContent(heatingProps)}
        </HeatingTimeline>
    );
};

export default BookingsOverview;
